import axios from "axios";

import { apiEndpoints } from "../constants/api.constants";
import { setNetworkIssue } from "../redux/slices/app.slices";
import store from "../redux/store";

const TOKEN_KEY_PAIRS = [
  {
    storage: "localStorage",
    accessKey: "accessToken",
    refreshKey: "refreshToken",
  },
  {
    storage: "sessionStorage",
    accessKey: "accessToken",
    refreshKey: "refreshToken",
  },
  {
    storage: "sessionStorage",
    accessKey: "accessToken_addCard",
    refreshKey: "refreshToken_addCard",
  },
  {
    storage: "sessionStorage",
    accessKey: "accessToken_addTapCard",
    refreshKey: "refreshToken_addTapCard",
  },
];

const getStorage = (name) =>
  name === "localStorage" ? localStorage : sessionStorage;

function getAccessToken() {
  for (const { storage, accessKey } of TOKEN_KEY_PAIRS) {
    const token = getStorage(storage).getItem(accessKey);
    if (token) return { token, storage, accessKey };
  }
  return null;
}

function getRefreshTokenSource() {
  for (const { storage, accessKey, refreshKey } of TOKEN_KEY_PAIRS) {
    const token = getStorage(storage).getItem(refreshKey);
    if (token) return { token, storage, accessKey, refreshKey };
  }
  return null;
}

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/`,
  headers: { "Content-Type": "application/json" },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const source = getAccessToken();
    if (source) {
      config.headers.Authorization = `Bearer ${source.token}`;
      // Attach source info so the response interceptor knows where to save refreshed tokens
      config._tokenSource = source;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Use the token source from the original request to find the matching refresh token
      // This ensures we save the new tokens back to the correct storage
      const requestSource = originalRequest._tokenSource;
      let source = null;

      if (requestSource) {
        // Find the refresh token from the same storage/key pair that the access token came from
        const matchingPair = TOKEN_KEY_PAIRS.find(
          (pair) =>
            pair.storage === requestSource.storage &&
            pair.accessKey === requestSource.accessKey,
        );
        if (matchingPair) {
          const refreshTokenValue = getStorage(matchingPair.storage).getItem(
            matchingPair.refreshKey,
          );
          if (refreshTokenValue) {
            source = { ...matchingPair, token: refreshTokenValue };
          }
        }
      }

      // Fallback to the original behavior if no matching source found
      if (!source) {
        source = getRefreshTokenSource();
      }

      const refreshToken = source?.token;

      if (refreshToken && source) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}${apiEndpoints.refreshToken}`,
            { refresh: refreshToken },
          );

          const newAccessToken = response.data?.access;
          const newRefreshToken =
            response.data?.refresh ?? response.data?.refresh_token;

          const storage = getStorage(source.storage);
          if (newAccessToken) storage.setItem(source.accessKey, newAccessToken);
          if (newRefreshToken)
            storage.setItem(source.refreshKey, newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }
    if (error?.response?.status > 500) {
      store.dispatch(setNetworkIssue(true));
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
